import moment from 'moment';
import {
    MOVIMENTACAO_UI_STATUS_CLASSNAMES,
    MOVIMENTACAO_UI_STATUS_LABELS
} from '../constants/movimentacoes.index.constants';
import { IMovimentacaoListItem } from '../../models/movimentacoes.service.model';
import { IMovimentacaoPeriodo } from '../../models/movimentacoes-periodos.service.model';
import {
    formatMovimentacaoEventoLabel,
    formatMovimentacaoMonthNumber,
    formatMovimentacaoReferenciaLabel,
    formatMovimentacaoVigenciaLabel,
    isMovimentacaoEventoMonetario,
    MOVIMENTACAO_REFERENCIA_MAX_LENGTH
} from '../../helpers/movimentacoes-value.helpers';

/**
 * Resolve o texto de status exibido na listagem.
 */
export function getMovimentacaoStatusLabel(situacao: number): string {
    return MOVIMENTACAO_UI_STATUS_LABELS[situacao] || 'Indefinido';
}

/**
 * Resolve o texto efetivo da badge de status a partir da entidade completa.
 */
export function getMovimentacaoDisplayStatusLabel(
    entity: IMovimentacaoListItem | null | undefined,
    workflowEnabled: boolean
): string {
    if (!entity) {
        return 'Indefinido';
    }

    if (isMovimentacaoAguardandoEstorno(entity)) {
        return entity.wkf_estado_titulo || 'Aguardando estorno';
    }

    if (shouldUseMovimentacaoWorkflowTitleAsPrimaryStatus(entity, workflowEnabled)) {
        return entity.wkf_estado_titulo || getMovimentacaoStatusLabel(Number(entity.situacao));
    }

    return getMovimentacaoStatusLabel(Number(entity.situacao));
}

/**
 * Resolve a classe visual usada para o status na listagem.
 */
export function getMovimentacaoStatusClass(situacao: number): string {
    return MOVIMENTACAO_UI_STATUS_CLASSNAMES[situacao] || 'label-default';
}

/**
 * Resolve a classe efetiva da badge de status a partir da entidade completa.
 */
export function getMovimentacaoDisplayStatusClass(
    entity: IMovimentacaoListItem | null | undefined,
    workflowEnabled: boolean
): string {
    if (!entity) {
        return 'label-default';
    }

    if (isMovimentacaoAguardandoEstorno(entity)) {
        return 'label-warning';
    }

    return getMovimentacaoStatusClass(Number(entity.situacao));
}

/**
 * Define se o título de workflow deve substituir o texto padrão da badge principal.
 */
export function shouldUseMovimentacaoWorkflowTitleAsPrimaryStatus(
    entity: IMovimentacaoListItem | null | undefined,
    workflowEnabled: boolean
): boolean {
    return !!workflowEnabled && !!entity?.wkf_estado_titulo;
}

function isMovimentacaoAguardandoEstorno(entity: IMovimentacaoListItem | null | undefined): boolean {
    return !!entity &&
        Number(entity.situacao) === 1 &&
        `${entity.wkf_estado || ''}` === 'AGUARDANDO_ESTORNO';
}

/**
 * Formata a referência exibida na tabela de movimentações.
 */
export function getMovimentacaoListReferenceLabel(entity: IMovimentacaoListItem): string {
    return formatMovimentacaoReferenciaLabel(entity.referencia, {
        maxLength: MOVIMENTACAO_REFERENCIA_MAX_LENGTH,
        ellipsis: '...'
    });
}

/**
 * Formata a data de pagamento exibida na tabela de movimentações.
 */
export function getMovimentacaoListPagamentoLabel(entity: IMovimentacaoListItem): string {
    const raw = entity?.datapagamento;

    if (!raw) {
        return '-';
    }

    const parsed = moment(raw, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);

    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
}

/**
 * Formata a rubrica exibida na tabela de movimentações.
 */
export function getMovimentacaoListEventoLabel(entity: IMovimentacaoListItem): string {
    return formatMovimentacaoEventoLabel(entity.evento);
}

/**
 * Resolve o nome principal da rubrica para exibição em linha dedicada.
 */
export function getMovimentacaoListEventoNome(entity: IMovimentacaoListItem): string {
    const nome = entity?.evento?.nome ? `${entity.evento.nome}`.trim() : '';
    const codigo = entity?.evento?.codigo ? `${entity.evento.codigo}`.trim() : '';

    return nome || codigo || '-';
}

/**
 * Resolve o código da rubrica para exibição auxiliar.
 */
export function getMovimentacaoListEventoCodigo(entity: IMovimentacaoListItem): string {
    return entity?.evento?.codigo ? `${entity.evento.codigo}`.trim() : '';
}

/**
 * Resolve o nome principal da lotação para exibição em linha dedicada.
 */
export function getMovimentacaoListLotacaoNome(entity: IMovimentacaoListItem): string {
    const nome = entity?.lotacao?.nome ? `${entity.lotacao.nome}`.trim() : '';
    const codigo = entity?.lotacao?.codigo ? `${entity.lotacao.codigo}`.trim() : '';

    return nome || codigo || '-';
}

/**
 * Resolve o código da lotação para exibição auxiliar.
 */
export function getMovimentacaoListLotacaoCodigo(entity: IMovimentacaoListItem): string {
    return entity?.lotacao?.codigo ? `${entity.lotacao.codigo}`.trim() : '';
}

/**
 * Formata a vigência exibida na tabela de movimentações.
 */
export function formatMovimentacaoListVigencia(entity: IMovimentacaoListItem): string {
    return formatMovimentacaoVigenciaLabel(entity, {
        unicaLabel: 'Apenas neste período',
        mensalLabel: 'Sempre',
        emptyLabel: 'Não informada',
        untilPrefix: 'Até'
    });
}

/**
 * Extrai o valor bruto da movimentação para exibição textual na tabela.
 */
export function getMovimentacaoListRawValue(entity: IMovimentacaoListItem): string {
    if (entity.valor === null || typeof entity.valor === 'undefined') {
        return '-';
    }

    return `${entity.valor}`;
}

/**
 * Indica se o valor da listagem deve ser exibido como monetário.
 */
export function isMovimentacaoListMonetaryValue(entity: IMovimentacaoListItem): boolean {
    const value = getMovimentacaoListRawValue(entity);
    return isMovimentacaoEventoMonetario(entity.evento) && value.indexOf(':') === -1;
}

/**
 * Resolve o período inicial da tela priorizando o mês atual aberto.
 */
export function resolveInitialMovimentacaoPeriodo(periodos: IMovimentacaoPeriodo[]): IMovimentacaoPeriodo | null {
    if (!periodos.length) {
        return null;
    }

    const currentMonth = new Date().getMonth() + 1;
    const openCurrentMonth = periodos.find((periodo) => periodo.mes === currentMonth && periodo.situacao === 1);
    if (openCurrentMonth) {
        return openCurrentMonth;
    }

    const firstOpen = periodos.find((periodo) => periodo.situacao === 1);
    if (firstOpen) {
        return firstOpen;
    }

    return periodos[0];
}

export { formatMovimentacaoMonthNumber };
